import { Notification, RichText, ConditionalContentList, ConditionalContent, Text } from '../survey';
import { stripHtmlAndMarkdown } from '../utils/textSanitizer';

type Content = Text | RichText | ConditionalContentList | ConditionalContent | undefined;

export type NotificationBody = Pick<Notification['initial_notification'],
  | 'message'
  | 'email_message'
  | 'email_subject'
>;

export const noMalformedTargetFieldsInNotifsRule = (notification: Readonly<Notification>) => {
  const malformedTargetFields = new Set<string>();

  findMalformedTargetFieldsInNotificationBody(notification.initial_notification, 'initial_notification', malformedTargetFields);

  if (notification.kind === 'Notification') {
    notification.followups?.forEach((flup) => {
      findMalformedTargetFieldsInNotificationBody(flup, `followup ${flup.suffix}`, malformedTargetFields);
    });
  }

  if (malformedTargetFields.size > 0) {
    const targetFieldsText = [...malformedTargetFields].join(', ');
    return {
      passed: false,
      message: `Found malformed target fields in notifications. Please try deleting these target fields and retyping them: ${targetFieldsText}`,
    };
  }

  return { passed: true };
};

const findMalformedTargetFieldsInNotificationBody = (notificationBody: NotificationBody, label: string, malformedTargetFields: Set<string>) => {
  const { message, email_subject, email_message } = notificationBody;

  findMalformedTargetFieldsInContent(message, `${label}.message`, malformedTargetFields);
  findMalformedTargetFieldsInContent(email_subject, `${label}.email_subject`, malformedTargetFields);
  findMalformedTargetFieldsInContent(email_message, `${label}.email_message`, malformedTargetFields);
};

const findMalformedTargetFieldsInContent = (content: Content, label: string, malformedTargetFields: Set<string>) => {
  if (!content) {
    return;
  }

  if (Array.isArray(content)) {
    return content.forEach((content) => {
      findMalformedTargetFieldsInContent(content, label, malformedTargetFields)
    });
  }

  if ('conditional' in content) {
    const components = [...content.components, ...(content.otherwise ?? [])];
    return components.forEach((content) => {
      findMalformedTargetFieldsInContent(content, label, malformedTargetFields);
    });
  }

  for (const lang in content) {
    const langContent = content[lang as keyof typeof content];

    // Skip underscore-prefixed metadata keys (e.g. _id, _trackedObjInfo), kind,
    // and any non-string value as a defensive guard.
    if (lang.startsWith('_') || lang === 'kind' || !langContent || typeof langContent !== 'string') {
      continue;
    }

    findMalformedTargetFieldsInString(langContent, `${label} (${lang})`, malformedTargetFields)
  }
};

const findMalformedTargetFieldsInString = (string: string, label: string, malformedTargetFields: Set<string>) => {
  const stringWithoutHtml = stripHtmlAndMarkdown(string);

  const targetFields = stringWithoutHtml.match(/\$[a-zA-Z][_a-zA-Z0-9]+/g);

  const targetFieldsWithCount: Record<string, number> = {};
  (targetFields ?? []).forEach((targetField) => {
    targetFieldsWithCount[targetField] ??= 0;
    targetFieldsWithCount[targetField]++;
  });

  Object.entries(targetFieldsWithCount).forEach(([targetField, count]) => {
    const targetFieldRegex = new RegExp(targetField.replace('$', '\\$') + '(\\W|$)', 'g');
    const countInOriginalString = string.match(targetFieldRegex)?.length ?? 0;

    if (countInOriginalString !== count) {
      malformedTargetFields.add(`${targetField} in ${label}`);
    }
  });

  return malformedTargetFields;
};
